import { RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';

const MIN_HEIGHT = 320;

const px = (value: string) => parseFloat(value) || 0;

/** Space rendered below `el` inside the document: bottom margins/paddings/borders of the
 *  element and its ancestors, plus any in-flow siblings that follow it. */
const getSpaceBelow = (el: HTMLElement) => {
  let space = px(getComputedStyle(el).marginBottom);
  let node: HTMLElement | null = el;

  while (node && node !== document.body) {
    let sibling = node.nextElementSibling as HTMLElement | null;
    while (sibling) {
      const style = getComputedStyle(sibling);
      if (style.position !== 'absolute' && style.position !== 'fixed' && style.display !== 'none') {
        space += sibling.getBoundingClientRect().height + px(style.marginTop) + px(style.marginBottom);
      }
      sibling = sibling.nextElementSibling as HTMLElement | null;
    }

    const parent: HTMLElement | null = node.parentElement;
    if (parent && parent !== document.body) {
      const style = getComputedStyle(parent);
      space += px(style.paddingBottom) + px(style.borderBottomWidth) + px(style.marginBottom);
    }
    node = parent;
  }

  return space;
};

/**
 * Sizes an inner scroll container so the whole page fits exactly in the viewport
 * (no outer page scroll). Height = viewport height - element's top - everything
 * rendered below it (margins, paddings, borders of its ancestors, following siblings).
 *
 * Re-measures on window resize, on any layout change of the page (e.g. the sticky
 * header collapsing) and after CSS transitions finish.
 */
export const useFillViewportHeight = (ref: RefObject<HTMLElement>, enabled = true) => {
  const [height, setHeight] = useState<number | undefined>(undefined);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || !enabled) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const top = rect.top + window.scrollY;
    const spaceBelow = getSpaceBelow(el);
    const next = Math.max(MIN_HEIGHT, Math.floor(window.innerHeight - top - spaceBelow));

    setHeight(prev => (prev === next ? prev : next));
  }, [ref, enabled]);

  // Measure before paint so the first frame already has the right height (no flash / jump).
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(document.body);

    window.addEventListener('resize', measure);
    document.addEventListener('transitionend', measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
      document.removeEventListener('transitionend', measure);
    };
  }, [measure, enabled]);

  return height;
};
